/**
 * Well-known ASN catalog — shared between matcher auto-classify
 * (PR 56) and the web Sources list display fallback (PR 53).
 *
 * Two consumers, one source of truth:
 *   - `packages/processor-worker/src/matcher/rules/known-forwarder-asn.ts`
 *     reads this to flip synthetic-fallback sources from a known
 *     forwarder ASN into auto-classified Forwarder Sources, so the
 *     ~990 review rows the Sources list inherited from the spine
 *     rollout (almost all Google/Microsoft/Yahoo forwarders) stop
 *     demanding manual classification.
 *   - `packages/web/src/pages/sources/index.tsx` uses the label
 *     for the display fallback when the API doesn't surface a
 *     `known_provider_name` — same map, no risk of drift between
 *     "we called it Google in the matcher decision" and "we called
 *     it AS15169 in the UI".
 *
 * Adding entries: only the heaviest-tail ASNs from the operator's
 * Sources list belong here. Goal is recognition, not coverage —
 * the long-tail "Unclassified" rows can keep their CIDR display.
 * Setting `kind: "forwarder"` triggers auto-classify in the
 * matcher; `kind: "esp"` / `"mixed"` do not (ESPs are usually
 * already in `known_providers` and `mixed` ASNs could be either).
 */
/**
 * Map keyed on ASN string in the form `AS<number>` (matching the
 * `source.asn` field and `dmarc_report_records.source_ip_asn`).
 */
export const WELL_KNOWN_ASNS = {
    // Major mailbox providers (forwarding behaviour dominates)
    AS15169: { label: "Google", kind: "forwarder" },
    AS8075: { label: "Microsoft", kind: "forwarder" },
    AS36647: { label: "Yahoo", kind: "forwarder" },
    AS10310: { label: "Yahoo", kind: "forwarder" },
    AS13335: { label: "Cloudflare", kind: "mixed" },
    // Hosting providers commonly used for forwarding
    AS16509: { label: "Amazon AWS", kind: "mixed" },
    AS14618: { label: "Amazon AWS", kind: "mixed" },
    AS396982: { label: "Google Cloud", kind: "mixed" },
    AS46606: { label: "Unified Layer", kind: "mixed" },
    // Email service providers (usually already classified via known_providers,
    // but listing them here gives a clean label fallback)
    AS11377: { label: "SendGrid", kind: "esp" },
    AS54489: { label: "Mailgun", kind: "esp" },
    AS27316: { label: "Mailchimp", kind: "esp" },
    AS6132: { label: "Postmark", kind: "esp" },
    AS62371: { label: "Proton Mail", kind: "forwarder" },
    // PR 57 — Mimecast (email security gateway / forwarder). The
    // matcher's known-provider-aligned + spf-only-fallback rules
    // don't fire for Mimecast traffic because forwarded mail keeps
    // the original sender's DKIM/SPF identity, not Mimecast's. The
    // `provider_mimecast` row in known_providers exists for direct
    // mimecast.com mail but doesn't help with the (much more common)
    // forwarding case — that's what these ASN entries are for.
    //
    // Multi-ASN: Mimecast operates several ASNs across regions. The
    // production data shows AS30031 on the rows we see; the others
    // are listed for completeness so a region change doesn't drop
    // recognition.
    AS30031: { label: "Mimecast", kind: "forwarder" },
    AS20943: { label: "Mimecast", kind: "forwarder" },
    AS27791: { label: "Mimecast", kind: "forwarder" },
    AS61102: { label: "Mimecast", kind: "forwarder" },
    AS60798: { label: "Mimecast", kind: "forwarder" },
    // Common European/Asian forwarders that show up in long-tail reports
    AS3209: { label: "Vodafone / Kabel Deutschland", kind: "forwarder" },
    AS3320: { label: "Deutsche Telekom", kind: "forwarder" },
    AS4134: { label: "China Telecom", kind: "forwarder" },
    AS4837: { label: "China Unicom", kind: "forwarder" },
    AS9498: { label: "Bharti Airtel", kind: "forwarder" },
    AS17676: { label: "SoftBank", kind: "forwarder" },
    AS7922: { label: "Comcast", kind: "forwarder" },
    AS701: { label: "Verizon", kind: "forwarder" },
};
/**
 * Display label lookup. Returns null when the ASN isn't in the map;
 * the caller falls back to the existing display chain
 * (cidr_bucket → asn → id).
 */
export function wellKnownAsnLabel(asn) {
    if (!asn)
        return null;
    return WELL_KNOWN_ASNS[asn.toUpperCase()]?.label ?? null;
}
/**
 * PR 56 — true iff `asn` is in the catalog with `kind === "forwarder"`.
 * Drives the `known-forwarder-asn` matcher rule's gating decision:
 * only ASNs that we are confident *are* forwarders auto-classify;
 * "mixed" or "esp" do not.
 */
export function isKnownForwarderAsn(asn) {
    if (!asn)
        return false;
    return WELL_KNOWN_ASNS[asn.toUpperCase()]?.kind === "forwarder";
}
/**
 * PR 56 — the auto-classification a matched ASN implies. Returns
 * `null` when the ASN isn't a known forwarder; otherwise the
 * `Classification` value the matcher should write on first attach
 * (and on flip-from-Unclassified for existing sources).
 *
 * Kept as a separate accessor (rather than baking `'Forwarder'` into
 * the matcher rule) so future ASN-kind expansions can map to other
 * classifications without rule-code churn.
 */
export function autoClassificationForAsn(asn) {
    return isKnownForwarderAsn(asn) ? "Forwarder" : null;
}
//# sourceMappingURL=well-known-asns.js.map